'use client';

import { LinearProgress, Typography } from '@mui/material';
import { DataGrid, type GridRenderCellParams, type GridSlotProps, useGridApiRef } from '@mui/x-data-grid';
import type { DataGridProps, GridValidRowModel } from '@mui/x-data-grid';
import { type GridCallbackDetails } from '@mui/x-data-grid/models/api';
import type { GridFeatureMode } from '@mui/x-data-grid/models/gridFeatureMode';
import { type GridPaginationModel } from '@mui/x-data-grid/models/gridPaginationProps';
import cn from 'classnames';
import pick from 'lodash/pick';
import { FC, useCallback, useLayoutEffect, useMemo, useState } from 'react';
import { z } from 'zod';

import { useEncodedPathname } from '../../../hooks/use-encoded-pathname';
import { useLocalStorageValue } from '../../../hooks/use-local-storage-value';
import { isDefined } from '../../../utils/utils';
import { OverflowingTextTip } from '../OverflowingTextTip';

import styles from './Table.module.scss';
import { TableFooter, TableFooterProps } from './TableFooter';
import { TableHeader, TableHeaderProps } from './TableHeader';
import { TableNoRowsOverlay } from './TableNoRowsOverlay';

const DEFAULT_ROWS_PER_PAGE = [5, 10, 20, 50, 100];

const Empty = () => null;
const LoadingOverlay = () => <LinearProgress />;

const tableUiSettingsSchema = z.object({
  columns: z.object({
    columnVisibilityModel: z.optional(z.record(z.boolean())),
  }),
});

export type TableProps<TRowModel extends GridValidRowModel> = Omit<
  DataGridProps<TRowModel>,
  'components' | 'componentsProps' | 'hideFooterPagination' | 'hideFooterSelectedRowCount' | 'pageSizeOptions'
> & {
  headerTitle?: string;
  headerTitlePrimary?: string;
  totalNumberRecords?: number;
  hideColumnsHeader?: boolean;
  hideHeader?: boolean;
  hideToolbar?: boolean;
  hideTotalRow?: boolean;
  totalRowProps?: TableFooterProps['totalRowProps'];
  toolbarProps?: TableHeaderProps['toolbarProps'];
  pageSizeOptions?: number[];
  onPageChange?: (page: number, details: GridCallbackDetails<'pagination'>) => void;
  onPageSizeChange?: (pageSize: number, details: GridCallbackDetails<'pagination'>) => void;
  page?: number;
  pageSize?: number;
  paginationMode?: GridFeatureMode;
  defaultLineCount?: number;
  saveUiSettings?: boolean;
};

export const Table: FC<TableProps<any>> = ({
  columns,
  headerTitle,
  headerTitlePrimary,
  hideHeader = false,
  hideToolbar = false,
  hideFooter = false,
  hideTotalRow = true,
  hideColumnsHeader = hideHeader ?? false,
  pagination = true,
  pageSizeOptions = DEFAULT_ROWS_PER_PAGE,
  totalRowProps,
  className,
  toolbarProps,
  onPageChange,
  onPageSizeChange,
  page,
  pageSize,
  paginationMode = 'server',
  defaultLineCount,
  saveUiSettings,
  ...props
}) => {
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: page ?? 0,
    pageSize: pageSize ?? 5,
  });

  const encodedPathname = useEncodedPathname();

  const { value: storageUiSettings, setValue: setStorageUiSettings } = useLocalStorageValue({
    key: `@table/${encodedPathname}/uiSettings`,
    schema: tableUiSettingsSchema,
  });

  const apiRef = useGridApiRef();

  const saveSettings = useCallback(() => {
    if (!saveUiSettings) {
      return;
    }

    const state = apiRef.current.exportState();

    setStorageUiSettings({
      columns: pick(state.columns, ['columnVisibilityModel']),
    });
  }, [apiRef, saveUiSettings, setStorageUiSettings]);

  const headerProps: TableHeaderProps = {
    content: (
      <>
        <Typography component="span">{headerTitle}</Typography>
        <Typography component="span" color="primary" sx={{ marginLeft: '3px' }}>
          {headerTitlePrimary}
        </Typography>
      </>
    ),
    hideToolbar,
    hideColumnsHeader,
    toolbarProps: {
      ...toolbarProps,
      saveSettings,
    },
  };

  const footerProps: TableFooterProps = {
    pagination,
    pageSizeOptions,
    hideTotalRow,
    hideFooter,
    totalRowProps,
  };

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel, details: GridCallbackDetails<'pagination'>) => {
      setPaginationModel((paginationModel) => {
        if (model.page === paginationModel.page && model.pageSize === paginationModel.pageSize) {
          return paginationModel;
        }

        if (model.page !== paginationModel.page) {
          onPageChange?.(model.page, details);
        }

        if (model.pageSize !== paginationModel.pageSize) {
          onPageSizeChange?.(model.pageSize, details);
        }

        return model;
      });
    },
    [onPageChange, onPageSizeChange]
  );

  const transformedColumns = useMemo(() => {
    return columns.map((column) => ({
      ...column,
      renderCell:
        column.renderCell ??
        ((props: GridRenderCellParams) => {
          const { value, row, api, colDef } = props;
          const formattedValue =
            value === undefined || !column.valueFormatter
              ? value
              : column.valueFormatter(value as never, row, colDef, {
                  current: api,
                });

          return <OverflowingTextTip lineCount={defaultLineCount}>{formattedValue}</OverflowingTextTip>;
        }),
    }));
  }, [columns, defaultLineCount]);

  useLayoutEffect(() => {
    if (
      (isDefined(page) && paginationModel.page !== page) ||
      (isDefined(pageSize) && paginationModel.pageSize !== pageSize)
    ) {
      setPaginationModel({
        page: page ?? paginationModel.page,
        pageSize: pageSize ?? paginationModel.pageSize,
      });
    }
  }, [page, pageSize, paginationModel.page, paginationModel.pageSize]);

  return (
    <DataGrid
      {...props}
      className={cn(className, {
        [styles.HideColumnsHeader]: hideColumnsHeader,
      })}
      apiRef={apiRef}
      initialState={storageUiSettings ?? undefined}
      columns={transformedColumns}
      hideFooter={hideFooter && hideTotalRow}
      slotProps={{
        toolbar: headerProps as GridSlotProps['toolbar'],
        footer: footerProps,
      }}
      slots={{
        loadingOverlay: LoadingOverlay,
        footer: TableFooter,
        noRowsOverlay: TableNoRowsOverlay,
        toolbar: hideHeader ? Empty : TableHeader,
        // row: TableRow,
      }}
      pagination={pagination}
      paginationModel={paginationModel}
      paginationMode={paginationMode}
      onPaginationModelChange={handlePaginationModelChange}
      //autoHeight={!isSmallSize}
    />
  );
};
